import { ActivityIndicator, Platform, View } from 'react-native';
import { Redirect, useLocalSearchParams } from 'expo-router';
import * as Linking from 'expo-linking';
import * as React from 'react';

import { Text } from '@sixam/ui/text';
import { completeAuthSessionFromUrl } from '@sixam/supabase/auth';
import { useSession } from '@/components/session-provider';
import { authDestination } from '@/lib/auth-return';

export default function AuthCallbackScreen() {
  const { returnTo } = useLocalSearchParams<{ returnTo?: string | string[] }>();
  const { session, isLoading } = useSession();
  const [error, setError] = React.useState<string | null>(null);
  const didComplete = React.useRef(false);

  React.useEffect(() => {
    if (didComplete.current || isLoading || session) {
      return;
    }

    let active = true;

    async function completeCallback() {
      const callbackUrl =
        Platform.OS === 'web' ? window.location.href : await Linking.getInitialURL();

      if (!active) {
        return;
      }

      if (!callbackUrl) {
        setError('Could not find the sign-in callback. Please try again.');
        return;
      }

      didComplete.current = true;
      try {
        await completeAuthSessionFromUrl(callbackUrl);
      } catch (callbackError) {
        setError(
          callbackError instanceof Error ? callbackError.message : 'Could not complete sign in.'
        );
      }
    }

    void completeCallback();

    return () => {
      active = false;
    };
  }, [isLoading, session]);

  if (session) {
    return <Redirect href={authDestination(returnTo)} />;
  }

  return (
    <View className="flex-1 items-center justify-center gap-4 bg-background px-8">
      {error ? (
        <Text className="text-center text-sm text-destructive" selectable>
          {error}
        </Text>
      ) : (
        <>
          <ActivityIndicator />
          <Text className="text-center text-sm text-muted-foreground">Completing sign in…</Text>
        </>
      )}
    </View>
  );
}
